import type { CSSProperties, FC, ReactNode } from 'react'

import { useState } from 'react'
import { Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { PointCloud } from '@/components/generals/point-cloud'
import { TextLink } from '@/components/generals/text-link'
import { PracticeSketch } from '@/components/practice/practice-sketch'

import { SERVICES } from '@/constants/practice'

/**
 * Five full-width service rows. A row opens when hovered with a mouse, and toggles on tap or click,
 * showing its spec table and a small drawing or point cloud.
 */
export const ServiceRows: FC = (): ReactNode => {
    const [open, setOpen] = useState<string | null>(SERVICES[0].id)

    return (
        <ul className='border-t border-hairline'>
            {SERVICES.map(service => {
                const isOpen = open === service.id
                const panelId = `service-${service.id}`

                return (
                    <li
                        key={service.id}
                        id={service.id}
                        onPointerEnter={event => {
                            if (event.pointerType === 'mouse') setOpen(service.id)
                        }}
                        className='group relative scroll-mt-20 border-b border-hairline'
                    >
                        <span
                            className={cn(
                                'absolute inset-y-0 left-0 w-[3px] origin-top bg-signal transition-transform duration-500 ease-instrument',
                                isOpen ? 'scale-y-100' : 'scale-y-0'
                            )}
                            aria-hidden='true'
                        />

                        <h3>
                            <button
                                type='button'
                                aria-expanded={isOpen}
                                aria-controls={panelId}
                                onClick={() => setOpen(isOpen ? null : service.id)}
                                className='grid w-full grid-cols-[2.5rem_minmax(0,1fr)_auto] items-baseline gap-x-4 gap-y-2 px-gutter py-8 text-left desk:grid-cols-[4rem_minmax(0,1.2fr)_minmax(0,1fr)_auto] desk:gap-x-8 desk:py-10'
                            >
                                <span className={cn('hud transition-colors duration-500', isOpen ? 'text-signal' : 'text-mute')}>
                                    {service.number}
                                </span>

                                <span className={cn('font-heading text-[clamp(28px,4vw,60px)] font-medium leading-[1.02] tracking-[-0.03em] transition-colors duration-500', isOpen ? 'text-ink' : 'text-dim group-hover:text-ink')}>
                                    {service.title}
                                </span>

                                <span className='col-start-2 text-[15px] leading-[1.55] text-ink/75 desk:col-start-auto desk:self-center'>
                                    {service.summary}
                                </span>

                                <span className='col-start-3 row-start-1 self-center desk:col-start-auto'>
                                    <Plus className={cn('size-5 text-ink transition-transform duration-500 ease-instrument', isOpen && 'rotate-45')} strokeWidth={1.25} aria-hidden='true' />
                                </span>
                            </button>
                        </h3>

                        <div
                            id={panelId}
                            role='region'
                            aria-label={service.title}
                            className={cn('grid transition-[grid-template-rows] duration-700 ease-instrument', isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')}
                        >
                            <div className='min-h-0 overflow-hidden' inert={!isOpen}>
                                <div data-reveal={isOpen ? 'in' : 'pre'} className='grid gap-10 px-gutter pb-12 desk:grid-cols-[4rem_minmax(0,1.2fr)_minmax(0,1fr)] desk:gap-x-8'>
                                    <span className='hidden desk:block' aria-hidden='true' />

                                    <div>
                                        <p className='reveal-fade max-w-[56ch] text-[15px] leading-[1.65] text-ink/85'>
                                            {service.body}
                                        </p>

                                        <dl className='reveal-fade mt-8 max-w-[560px] border-t border-hairline' style={{ '--delay': '120ms' } as CSSProperties}>
                                            {service.specs.map(spec => (
                                                <div key={spec.label} className='flex items-baseline justify-between gap-6 border-b border-hairline py-3'>
                                                    <dt className='hud shrink-0 text-mute'>
                                                        {spec.label}
                                                    </dt>

                                                    <dd className='hud text-right text-ink'>
                                                        {spec.value}
                                                    </dd>
                                                </div>
                                            ))}
                                        </dl>

                                        <div className='reveal-fade mt-8 flex flex-wrap gap-x-8 gap-y-3' style={{ '--delay': '220ms' } as CSSProperties}>
                                            {service.examples.map(example => (
                                                <TextLink key={example.href} href={example.href}>
                                                    {example.label}
                                                </TextLink>
                                            ))}
                                        </div>
                                    </div>

                                    <div className='relative aspect-[4/3] w-full border border-hairline desk:max-w-[520px] desk:justify-self-end'>
                                        {service.visual.kind === 'cloud'
                                            ? isOpen && (
                                                <PointCloud
                                                    shape={service.visual.shape}
                                                    label={service.visual.label}
                                                    budget='small'
                                                    rotate='always'
                                                    rotateSpeed={6}
                                                    hoverBoost={3}
                                                    effects
                                                    frame
                                                    className='absolute inset-0'
                                                />
                                            )
                                            : (
                                                <div className='absolute inset-0 p-4'>
                                                    <PracticeSketch sketch={service.visual.sketch} label={service.visual.label} />
                                                </div>
                                            )}

                                        <span className='hud pointer-events-none absolute left-3 top-3 z-[3] text-mute' aria-hidden='true'>
                                            Fig. {service.number}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </li>
                )
            })}
        </ul>
    )
}
